import * as z from "zod";

/**
 * Whether a team requires a CTX billing plan. Legacy teams start `unset`
 * (not billed, not yet classified); admin migrates them to one of the others.
 */
export const contextualPlanStatusSchema = z.enum([
  "unset",
  "no_plan_required",
  "needs_plan",
]);
export type ContextualPlanStatus = z.infer<typeof contextualPlanStatusSchema>;

/**
 * The active CTX plan.
 *
 * - `usage`: volume-tiered per-session pricing with a monthly floor (the
 *   default a merchant enables).
 * - `overage`: a fixed monthly base fee that includes a session allowance,
 *   with a per-session overage rate beyond it. Every term is negotiated per
 *   team and entered manually in redo admin — there are no default values.
 *
 * Tagged union so variants can be added without migrating existing rows.
 */
export const contextualPlanSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("usage") }),
  z.object({
    type: z.literal("overage"),
    /** Fixed monthly base fee, in USD micros. */
    baseMicros: z.number().int().nonnegative(),
    /** Sessions covered by the base fee before the overage rate applies. */
    includedSessions: z.number().int().nonnegative(),
    /** Per-session price beyond `includedSessions`, in USD micros. */
    overageRateMicros: z.number().int().nonnegative(),
  }),
]);
export type ContextualPlan = z.infer<typeof contextualPlanSchema>;

export const contextualBillingSettingsSchema = z.object({
  planStatus: contextualPlanStatusSchema,
  /** End of the free trial; while `now < trialEndsAt` usage is not charged. */
  trialEndsAt: z.date().nullish(),
  plan: contextualPlanSchema.nullish(),
  /**
   * When billing starts. Required for the engine to bootstrap the first
   * billing cycle for a team — without it the initial-subscription setup
   * bails and the team never begins billing. Set on plan activation.
   */
  startDate: z.date().nullish(),
});
export type ContextualBillingSettings = z.infer<
  typeof contextualBillingSettingsSchema
>;
